import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Send } from 'lucide-react';

const TeamSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const teamMembers = [
    {
      name: 'Wassim. H',
      title: 'CEO',
      telegramUrl: 'https://t.me/WH_quicksync',
      image: '/wassim.png',
    },
    {
      name: 'Omar. H',
      title: 'CTO',
      telegramUrl: 'https://t.me/OH_quicksync',
      image: 'https://customer-assets.emergentagent.com/job_f66dc093-173f-4bcc-bf21-1c61e3bc9a7a/artifacts/hneoxaeg_photo_2026-01-01_15-52-25.jpg',
    },
    {
      name: 'Dino. N',
      title: 'Advisor',
      telegramUrl: null,
      image: 'https://customer-assets.emergentagent.com/job_f66dc093-173f-4bcc-bf21-1c61e3bc9a7a/artifacts/42pqsorm_photo_2025-06-23_18-33-00.jpg',
    },
    {
      name: 'Martins. B',
      title: 'UX/UI',
      telegramUrl: null,
      image: 'https://customer-assets.emergentagent.com/job_f66dc093-173f-4bcc-bf21-1c61e3bc9a7a/artifacts/py4t4pwb_images.jpg',
    },
    {
      name: 'Hamza. M',
      title: 'Dev Team',
      telegramUrl: null,
      image: 'https://customer-assets.emergentagent.com/job_quicksyncapp/artifacts/5e2dhblw_image.png',
    },
    {
      name: 'Mohd. F',
      title: 'Technical Consultant',
      telegramUrl: null,
      image: 'https://customer-assets.emergentagent.com/job_f66dc093-173f-4bcc-bf21-1c61e3bc9a7a/artifacts/ijkuvwjb_image.png',
    },
    {
      name: 'Neo. M',
      title: 'Partnerships',
      telegramUrl: null,
      image: 'https://customer-assets.emergentagent.com/job_quicksyncapp/artifacts/zhb9ysw6_image.png',
    },
    {
      name: 'QS Community',
      title: '',
      telegramUrl: 'https://t.me/quick_sync',
    },
  ];

  // Responsive items per view
  const getItemsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) return 2; // mobile
      if (window.innerWidth < 1024) return 3; // tablet
      return 4; // desktop
    }
    return 4;
  };

  const [itemsPerView, setItemsPerView] = React.useState(4);
  
  React.useEffect(() => {
    const handleResize = () => {
      setItemsPerView(getItemsPerView());
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, teamMembers.length - itemsPerView);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  // Reset index if it exceeds maxIndex after resize
  React.useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [maxIndex, currentIndex]);

  // Calculate card width based on items per view
  const getCardWidth = () => {
    if (itemsPerView === 2) return 'w-[calc(50%-10px)]';
    if (itemsPerView === 3) return 'w-[calc(33.333%-14px)]';
    return 'w-[calc(25%-15px)]';
  };

  return (
    <section className="relative bg-white py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header with Title and Navigation */}
        <div className="flex items-center justify-between mb-8 md:mb-12">
          {/* Title */}
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold">
            <span className="text-gray-900">Meet the </span>
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Team</span>
          </h2>

          {/* Navigation Arrows */}
          <div className="flex items-center gap-2 md:gap-3">
            <button 
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                currentIndex === 0 
                  ? 'border-gray-200 text-gray-300 cursor-not-allowed' 
                  : 'border-gray-300 text-gray-600 hover:border-emerald-500 hover:text-emerald-600 hover:bg-emerald-50'
              }`}
            >
              <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" strokeWidth={2} />
            </button>
            <button 
              onClick={handleNext}
              disabled={currentIndex >= maxIndex}
              className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                currentIndex >= maxIndex 
                  ? 'border-gray-200 text-gray-300 cursor-not-allowed' 
                  : 'border-gray-300 text-gray-600 hover:border-emerald-500 hover:text-emerald-600 hover:bg-emerald-50'
              }`}
            >
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5" strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Horizontal Carousel */}
        <div className="relative overflow-hidden">
          <div 
            className="flex transition-transform duration-500 ease-out gap-3 md:gap-5"
            style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView + (itemsPerView === 2 ? 1 : 1.5))}%)` }}
          >
            {teamMembers.map((member, index) => (
              <div 
                key={index}
                className={`flex-shrink-0 ${getCardWidth()}`}
              >
                {member.name === 'QS Community' ? (
                  /* Special Community Card */
                  <a 
                    href={member.telegramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100 hover:border-emerald-200 transition-all duration-300 hover:shadow-xl h-[180px] md:h-[220px] flex flex-col"
                  >
                    {/* QS Logo Icon */}
                    <div className="mb-3 md:mb-5">
                      <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl overflow-hidden border-2 border-gray-100 shadow-md">
                        <img 
                          src="/quicksync-logo.png" 
                          alt="QS Community"
                          className="w-full h-full object-contain bg-white p-1.5 md:p-2"
                        />
                      </div>
                    </div>

                    {/* Name */}
                    <h3 className="text-base md:text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-1">
                      QS Community
                    </h3>
                    
                    {/* Description */}
                    <p className="text-xs md:text-sm text-gray-600 mb-4 md:mb-6">
                      Join our growing community
                    </p>

                    {/* Join Button */}
                    <div className="mt-auto pt-1 md:pt-2">
                      <span className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm font-medium text-emerald-600 group-hover:text-emerald-700 transition-colors">
                        <span>Join us</span>
                        <svg className="w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </span>
                    </div>
                  </a>
                ) : (
                  /* Regular Team Member Card */
                  <div className="group bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100 hover:border-emerald-200 transition-all duration-300 hover:shadow-xl h-[180px] md:h-[220px] flex flex-col">
                    {/* Profile Image or Initial */}
                    <div className="mb-3 md:mb-5">
                      {member.image ? (
                        <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl overflow-hidden border-2 border-gray-100 shadow-md">
                          <img 
                            src={member.image} 
                            alt={member.name}
                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center border-2 border-gray-100">
                          <span className="text-xl md:text-2xl font-bold text-emerald-600">
                            {member.name.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Name */}
                    <h3 className="text-base md:text-xl font-bold text-gray-900 mb-0.5 md:mb-1">{member.name}</h3>
                    
                    {/* Title */}
                    {member.title && (
                      <p className="text-xs md:text-sm font-medium bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4 md:mb-6">
                        {member.title}
                      </p>
                    )}

                    {/* Telegram */}
                    {member.telegramUrl && (
                      <a 
                        href={member.telegramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-auto pt-1 md:pt-2 inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-gray-500 hover:text-emerald-600 transition-colors"
                      >
                        <Send className="w-3 h-3 md:w-4 md:h-4" strokeWidth={2} />
                        <span>Telegram</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Progress Dots */}
        <div className="flex items-center justify-center gap-1.5 md:gap-2 mt-6 md:mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-emerald-500 w-4 md:w-6' 
                  : 'bg-gray-300 hover:bg-gray-400 w-2'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
